import type { Figure } from "@howcomet/shared";
import type { FigureProps } from "./registry";

const COLORS = [
  "var(--color-cyan)",
  "var(--color-mint)",
  "var(--color-lilac)",
  "var(--color-pink)",
  "var(--color-gold)",
];

/** Readable at a glance: 1392000 → "1,392,000", 12.742 → "12.7". */
function show(value: number): string {
  const rounded = value >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  return rounded.toLocaleString("en-GB");
}

/**
 * Bars in true proportion, which is the entire point and also the problem.
 *
 * "The Sun is 109 times wider than the Earth" is a sentence a child reads and
 * does not feel. A bar 109 times longer is one they do — but at that ratio the
 * Earth's bar is under one percent of the width, and past a few hundred it
 * rounds to nothing.
 *
 * So: bars stay honestly proportional and never shrink below a hairline, and
 * once the spread is large enough that the eye can no longer judge it, the
 * multiple is stated in words next to the smallest bar. The picture keeps
 * telling the truth; the text stops it being a truth nobody can read.
 */
export function ScaleFigure({ figure }: FigureProps<Figure>) {
  const labels = figure.labels ?? [];
  const values = figure.values ?? [];
  if (labels.length < 2 || values.length !== labels.length) return null;

  const max = Math.max(...values);
  const min = Math.min(...values);
  if (max <= 0) return null;
  const ratio = max / min;
  const wideSpread = ratio >= 25;

  return (
    <div
      className="flex flex-col gap-2"
      role="img"
      aria-label={labels.map((l, i) => `${l}: ${show(values[i]!)}`).join(". ")}
    >
      {labels.map((label, i) => {
        const value = values[i]!;
        return (
          <div key={label}>
            <div className="mb-[3px] flex items-baseline justify-between gap-2 text-[14px]">
              <span className="text-cream/85">{label}</span>
              <span className="disp shrink-0 text-cream/60">{show(value)}</span>
            </div>
            <div className="h-[16px] w-full overflow-hidden rounded-full bg-cream/10">
              <div
                className="band-in h-full rounded-full"
                style={{
                  // Never zero: a bar with no length reads as missing data
                  // rather than as "very small next to the other one".
                  width: `${Math.max((value / max) * 100, 1.2)}%`,
                  background: COLORS[i % COLORS.length],
                  animationDelay: `${i * 0.1}s`,
                }}
              />
            </div>
            {wideSpread && value === min && (
              <p className="mt-1 text-[13px] text-cream/55">
                {show(max / min)}× smaller than the biggest bar
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
